// Vista previa de plantillas en el admin: el mismo cálculo que hace el motor (agent-runtime/src/tools/template.ts) para mostrar el
// texto ya armado con los datos de la última prueba o los datos fijados. Si cambias un método allá, cámbialo aquí también.
/** Las plantillas de un texto (igual que `scan` del motor): `{{= código }}` respeta llaves y comillas dentro del código. */
export function scan(text) {
  const out = []; let i = 0; text = String(text || '');
  while ((i = text.indexOf('{{', i)) >= 0) {
    let j = i + 2;
    while (text[j] === ' ' || text[j] === '\t' || text[j] === '\n') j++;
    if (text[j] === '=') {
      let depth = 0; let q = null; let end = -1;
      for (let k = j + 1; k < text.length; k++) {
        const ch = text[k];
        if (q) { if (ch === '\\') k++; else if (ch === q) q = null; continue; }
        if (ch === '"' || ch === "'" || ch === '`') { q = ch; continue; }
        if (ch === '{') depth++;
        else if (ch === '}') { if (depth === 0 && text[k + 1] === '}') { end = k; break; } depth = Math.max(0, depth - 1); }
      }
      if (end < 0) { i += 2; continue; }
      out.push({ start: i, end: end + 2, expr: text.slice(i + 2, end).trim() });
      i = end + 2;
    } else {
      const close = text.indexOf('}}', i + 2);
      if (close < 0) break;
      const inner = text.slice(i + 2, close);
      if (inner.includes('{')) { i += 2; continue; }
      out.push({ start: i, end: close + 2, expr: inner.trim() });
      i = close + 2;
    }
  }
  return out.filter((t) => t.expr);
}
export const hasCode = (text) => scan(text).some((t) => t.expr.startsWith('='));
/** Raíces que el motor llena en las instrucciones (PROMPT_ROOTS de agent-runtime/src/agent/preTools.ts); lo demás queda tal cual. */
export const PROMPT_ROOTS = ['contacto', 'mensaje', 'ahora', 'datos', 'agente'];
const CONTACT_FIELDS = ['nombre', 'telefono', 'canal', 'chat_id', 'id'];
const DATE_FORMATS = ['corta', 'larga', 'completa', 'hora'];
const N8N_HINT = 'Usa {{contacto.nombre}}, {{contacto.telefono}}, {{mensaje}} o, para la fecha y hora de hoy, {{ahora | fecha:"completa"}}.';
/**
 * ¿El motor entiende esta variable de un prompt? Devuelve el motivo en español si NO (el agente la recibiría escrita tal cual) o null.
 * Igual que `explainTemplate` del motor (agent-runtime/src/assist/check.ts): si cambias uno, cambia el otro.
 * `pre`: nombres de los datos previos del agente; `methods`: nombres de métodos válidos.
 */
export function explainTemplate(expr, pre = [], methods = null) {
  const e = String(expr || '').trim();
  if (e.startsWith('=')) return null;
  if (/\$/.test(e.split('|')[0])) return `Es una expresión de n8n: el motor no la entiende y el agente la recibiría escrita tal cual. ${N8N_HINT}`;
  const [root = '', ...rest] = e.split('|')[0].trim().split('.');
  if (!PROMPT_ROOTS.includes(root.trim())) return `El motor no tiene un dato «${root.trim() || e.slice(0, 30)}». Los que existen: contacto, mensaje, ahora, agente${pre.length ? ' y datos.<nombre>' : ''}. ${N8N_HINT}`;
  const field = rest[0]?.trim();
  if (root === 'contacto' && (!field || !CONTACT_FIELDS.includes(field))) return `«contacto» no tiene ${field ? `«${field}»` : 'ese campo'}. Tiene: ${CONTACT_FIELDS.join(', ')}.`;
  if (root === 'agente' && field !== 'id') return '«agente» solo tiene «id».';
  if (root === 'datos') {
    if (!field) return 'Falta el nombre del dato previo: {{datos.<nombre>.texto}}.';
    if (!pre.includes(field)) return `No hay un dato previo llamado «${field}»${pre.length ? `. Hay: ${pre.join(', ')}` : ' (el agente no tiene datos previos)'}.`;
  }
  if (methods) for (const m of e.split('|').slice(1)) { const n = m.split(':')[0].trim(); if (n && !methods.has(n)) return `No existe el método «${n}».`; }
  const fmt = /\|\s*fecha\s*:\s*(["']?)([^"'|}\s]*)\1/u.exec(e);
  if (fmt && !DATE_FORMATS.includes(fmt[2])) return `«${fmt[2]}» no es un formato de fecha: usa "corta", "larga", "completa" (con hora) u "hora".`;
  return null;
}

export const isPromptExpr = (expr) => expr.startsWith('=') || PROMPT_ROOTS.includes(expr.split(/[.|\s]/)[0]);
const PATH = /^[A-Za-z0-9_.-]+$/;
const TZ = 'America/Mexico_City';

export function lookup(scope, path) {
  let cur = scope;
  for (const part of path.split('.')) {
    if (cur === null || cur === undefined) return undefined;
    if (Array.isArray(cur)) { if (!/^\d+$/.test(part)) return undefined; cur = cur[Number(part)]; continue; }
    if (typeof cur !== 'object' || !Object.prototype.hasOwnProperty.call(cur, part)) return undefined;
    cur = cur[part];
  }
  return cur;
}
const asText = (v) => (v === null || v === undefined ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v));
const isEmpty = (v) => v === null || v === undefined || v === '' || (Array.isArray(v) && !v.length) || (typeof v === 'object' && !Array.isArray(v) && !Object.keys(v).length);
const num = (v) => { const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v.trim()) : NaN; return Number.isFinite(n) ? n : null; };
const date = (v) => { if (v === null || v === undefined || v === '') return null; const d = new Date(typeof v === 'number' ? v : String(v)); return Number.isNaN(d.getTime()) ? null : d; };

const PIPES = {
  mayusculas: (v) => asText(v).toLocaleUpperCase('es-MX'),
  minusculas: (v) => asText(v).toLocaleLowerCase('es-MX'),
  recortar: (v) => asText(v).trim(),
  limite: (v, a) => { const s = asText(v); const n = Math.max(1, Math.min(Number(a) || 100, 5000)); return s.length > n ? `${s.slice(0, n)}…` : s; },
  defecto: (v, a) => (isEmpty(v) ? a ?? '' : v),
  cantidad: (v) => (Array.isArray(v) ? v.length : typeof v === 'string' ? v.length : v && typeof v === 'object' ? Object.keys(v).length : 0),
  primero: (v) => (Array.isArray(v) ? v[0] ?? null : v),
  ultimo: (v) => (Array.isArray(v) ? v[v.length - 1] ?? null : v),
  campo: (v, a) => (Array.isArray(v) ? v.map((x) => (a ? lookup(x, a) : x)) : a ? lookup(v, a) : v),
  unir: (v, a) => (Array.isArray(v) ? v.map(asText).join(a ?? ', ') : asText(v)),
  numero: (v) => num(v),
  redondear: (v, a) => { const n = num(v); const d = Math.max(0, Math.min(Number(a) || 0, 6)); return n === null ? null : Number(n.toFixed(d)); },
  moneda: (v) => { const n = num(v); return n === null ? '' : new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n); },
  fecha: (v, a) => {
    const d = date(v); if (!d) return '';
    if (a === 'completa') return `${new Intl.DateTimeFormat('es-MX', { dateStyle: 'long', timeZone: TZ }).format(d)} ${new Intl.DateTimeFormat('es-MX', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: TZ }).format(d)}`;
    const opts = a === 'larga' ? { dateStyle: 'long' } : a === 'hora' ? { dateStyle: 'short', timeStyle: 'short' } : { dateStyle: 'short' };
    return new Intl.DateTimeFormat('es-MX', { ...opts, timeZone: TZ }).format(d);
  },
  existe: (v) => !isEmpty(v),
  json: (v) => JSON.stringify(v ?? null),
  texto: (v) => asText(v),
};

function split(expr) {
  const out = []; let cur = ''; let q = null;
  for (const ch of expr) {
    if (q) { cur += ch; if (ch === q) q = null; continue; }
    if (ch === '"' || ch === "'") { q = ch; cur += ch; continue; }
    if (ch === '|') { out.push(cur.trim()); cur = ''; continue; }
    cur += ch;
  }
  out.push(cur.trim());
  return out;
}
const arg = (raw) => { if (raw === undefined) return null; const t = raw.trim(); return /^(["']).*\1$/s.test(t) ? t.slice(1, -1) : t; };

/** Valor de una expresión, y si su ruta existe en los datos (para marcar las que no). */
export function evaluate(expr, scope) {
  if (expr.startsWith('=')) return { value: null, missing: false, code: true };
  const [path, ...pipes] = split(expr);
  if (!path || !PATH.test(path)) return { value: null, missing: true };
  let v = lookup(scope, path);
  const missing = v === undefined;
  for (const p of pipes) {
    const i = p.indexOf(':');
    const name = (i < 0 ? p : p.slice(0, i)).trim();
    const fn = Object.prototype.hasOwnProperty.call(PIPES, name) ? PIPES[name] : undefined;
    if (!fn) return { value: null, missing, badMethod: name };
    v = fn(v === undefined ? null : v, i < 0 ? null : arg(p.slice(i + 1)));
  }
  return { value: v === undefined ? null : v, missing };
}

export function render(template, scope) {
  const toks = scan(template);
  if (toks.length === 1 && !template.slice(0, toks[0].start).trim() && !template.slice(toks[0].end).trim()) return evaluate(toks[0].expr, scope).value;
  let out = ''; let last = 0;
  for (const t of toks) { out += template.slice(last, t.start) + asText(evaluate(t.expr, scope).value); last = t.end; }
  return out + template.slice(last);
}

/**
 * Partes para mostrar el texto armado resaltando lo que vino de un dato: [{ text, from?: '{{…}}', missing? }].
 * `missing`: la ruta no existe en los datos de la prueba (o el paso no corrió).
 */
export function previewParts(template, scope, { keep } = {}) {
  const parts = []; let last = 0;
  for (const t of scan(template)) {
    if (keep && !keep(t.expr)) continue;   // (instrucciones) lo que el motor no llena queda como texto
    if (t.start > last) parts.push({ text: template.slice(last, t.start) });
    const from = template.slice(t.start, t.end);
    if (t.expr.startsWith('=')) parts.push({ text: '', from, code: true });   // el código lo calcula el motor
    else { const r = evaluate(t.expr, scope); parts.push({ text: asText(r.value), from, missing: r.missing || Boolean(r.badMethod) }); }
    last = t.end;
  }
  if (last < String(template || '').length) parts.push({ text: template.slice(last) });
  return parts;
}
